import * as DOMPurify from "dompurify";
import * as React from "react";
import { TOP_NAV_HEIGHT } from "../NavigationUtils/NavigationUtils";
import { throttle } from "../throttle/throttle";

/**
 * These tags are always forbidden in setInnerHtmlSanitized.
 * See http://go/dompurify-security
 * The default allowed tags are the set of tags defined at
 * https://github.com/cure53/DOMPurify/blob/main/src/tags.js
 * excluding FORBIDDEN_TAGS.
 */
const FORBIDDEN_TAGS: Readonly<string[]> = ["style"];

/**
 * WARNING: Any direct innerHTML changes must be done via this function!
 * Setting innerHTML without sanitizing immediately prior creates a risk for
 * XSS attacks. Never set innerHTML or use dangeroustlySetInnerHTML directly!
 * Sets innerHTML on React element (via dangerouslySetInnerHTML) or DOM Element
 * (via element.innerHTML.)
 *
 * sanitize=false should only be set for content that comes directly from the
 * Kaggle CMS (e.g., pages that use jotforms).
 *
 * Overloads are provided so the return value is typed
 * properly without the need for generics:
 */
export function setInnerHtmlSanitized(
  element: React.ReactElement,
  html: string,
  allowUnsanitized?: boolean
): React.ReactElement;
export function setInnerHtmlSanitized(
  element: Element,
  html: string,
  allowUnsanitized?: boolean
): Element;
export function setInnerHtmlSanitized(
  element: React.ReactElement | Element,
  html: string,
  allowUnsanitized = false
) {
  if (!allowUnsanitized) {
    html = DOMPurify.sanitize(html, {
      FORBID_TAGS: [...FORBIDDEN_TAGS],
    });
  }
  if ("innerHTML" in element) {
    element.innerHTML = html;
    return element;
  } else {
    return React.cloneElement(element, {
      dangerouslySetInnerHTML: { __html: html },
    });
  }
}

export function isScreenUnderWidth(widthInPx: number) {
  // Added fallback to handle errors that occur in unit test context.
  try {
    return window.matchMedia(`(max-width: ${widthInPx}px)`).matches;
  } catch (err) {
    // tslint:disable-next-line: no-console
    console.warn("Error occurred calling window.matchMedia.", err.message);
    return false;
  }
}

export function useMobileWidth(widthInPx: number) {
  const [isMobileWidth, setIsMobileWidth] = React.useState(
    isScreenUnderWidth(widthInPx)
  );

  React.useEffect(() => {
    const listener = throttle(
      () => setIsMobileWidth(isScreenUnderWidth(widthInPx)),
      500
    );
    window.addEventListener("resize", listener);
    return () => window.removeEventListener("resize", listener);
  });

  return isMobileWidth;
}

/**
 * Returns the BoundingClientRect object of the viewport (what
 * window.getBoundingClientRect() would return if that API existed)
 */
export function getViewportRect(): DOMRect {
  const html = document.documentElement;
  return makeDomRect({
    top: 0,
    bottom: html.clientHeight,
    left: 0,
    right: html.clientWidth,
  });
}

/**
 * Returns the intersection rectangle of two DOMRect objects, typically
 * generated with element.getBoundingClientRect()
 */
export function getIntersectionRect(rect1: DOMRect, rect2: DOMRect): DOMRect {
  return makeDomRect({
    top: Math.max(rect1.top, rect2.top),
    bottom: Math.min(rect1.bottom, rect2.bottom),
    left: Math.max(rect1.left, rect2.left),
    right: Math.min(rect1.right, rect2.right),
  });
}

/**
 * Whether the first rectangle is contained entirely within the second
 * rectangle. DOMRect instances are generated using
 * element.getBoundingClientRect()
 */
export function containsRect(rect1: DOMRect, rect2: DOMRect) {
  return (
    rect1.top >= rect2.top &&
    rect1.left >= rect2.left &&
    rect1.right <= rect2.right &&
    rect1.bottom <= rect2.bottom
  );
}

/**
 * Makes a DOMRect object from top/bottom/left/right points.
 */
export function makeDomRect({
  top,
  bottom,
  left,
  right,
}: {
  top: number;
  bottom: number;
  left: number;
  right: number;
}): DOMRect {
  const rect = {
    top,
    bottom,
    left,
    right,
    x: left,
    y: top,
    height: bottom - top,
    width: right - left,
  };

  return { ...rect, toJSON: () => rect };
}

interface ScrollOptions {
  topOffset?: number;
  smooth?: boolean;
}

export const getSiteScrollContainer = () =>
  document.getElementById("site-content");

export function scrollToOffset(
  topOffset: number,
  container: Element | null = getSiteScrollContainer(),
  options?: ScrollOptions
) {
  if (!container) {
    return;
  }
  if (options?.smooth) {
    try {
      container.scroll({
        top: topOffset - (options?.topOffset ?? 0),
        left: container.scrollLeft,
        behavior: "smooth",
      });
      return;
    } finally {
    }
  }
  container.scrollTop = topOffset;
}
export function scrollToElement(
  target: Element | null,
  container: Element | null = getSiteScrollContainer(),
  options?: ScrollOptions
) {
  if (!container || !target) {
    return;
  }

  const topOffset =
    options?.topOffset ??
    (container === getSiteScrollContainer() ? TOP_NAV_HEIGHT : 0);
  const scrollTop = target.getBoundingClientRect().top - topOffset;
  if (options?.smooth) {
    try {
      container.scroll({
        top: scrollTop,
        left: container.scrollLeft,
        behavior: "smooth",
      });
      return;
    } finally {
    }
  }
  container.scrollTop = target.getBoundingClientRect().top - topOffset;
}

export function scrollToId(
  id: string | number,
  container: Element | null = getSiteScrollContainer(),
  options?: ScrollOptions
) {
  scrollToElement(document.getElementById(String(id)), container, options);
}

export function scrollToHash(
  container: Element | null = getSiteScrollContainer(),
  options?: ScrollOptions
) {
  scrollToId(window.location.hash.slice(1), container, options);
}

export const copyToClipboard = (text: string) => {
  const copyFunction = (e: ClipboardEvent) => {
    (e.clipboardData as DataTransfer).setData("text/plain", text);
    e.preventDefault();
    document.removeEventListener("copy", copyFunction);
  };

  document.addEventListener("copy", copyFunction);
  document.execCommand("copy");
};

export function pathnameToPathArray(pathname: string) {
  return pathname
    .split("/")
    .filter(p => !!p)
    .map(e => e.toLowerCase());
}
